import { observer } from 'mobx-react-lite';
import React from 'react';
import {
  FocusContext,
  doesFocusableExist,
  setFocus,
  useFocusable,
  getCurrentFocusKey
} from '@noriginmedia/norigin-spatial-navigation';
import clsx from 'clsx';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import Button from '@smart-tv/components/button/button';
import styles from '../episode-selector.module.scss';
import { rootStore } from '../_stores/store-root';
import { uiStore } from '../_stores/store-ui';
import { useStreamActions } from '../_stores/useStreamActions';
import { EpisodeExtended } from '../types';
import { FocusKeysSheet } from '../sheet-helpers';
import { useTimeout } from '@smart-tv/hooks/use-timeout';

import SheetEpisodeSelectorSeasonTabs from './sheet-episode-selector-season-tabs';
import SheetEpisodeSelectorList from './sheet-episode-selector-list';
import SheetEpisodeSelectorDetail from './sheet-episode-selector-detail';
import { useSetAtom } from 'jotai';
import { NPVRStreamAtom } from '@smart-tv/atoms';
import { useQuery } from '@smart-tv/hooks/use-query/use-query';
import { QueryParams } from '@smart-tv/components/program-sheet/components/sheet';

import { decodeProgramSheetParams } from '@smart-tv/utils/routing/decode-program-sheet-params/decode-program-sheet-params';

// ─── Tab button ───────────────────────────────────────────────

interface TabButtonProps {
  label: string;
  active: boolean;
  focusKey: string;
  onClick: () => void;
  onArrowPress?: (direction: string) => boolean;
  onNavFocus?: () => void;
}

const TabButton: React.FC<TabButtonProps> = ({
  label,
  active,
  focusKey,
  onClick,
  onArrowPress,
  onNavFocus
}) => (
  <Button
    focusKey={focusKey}
    selected={active}
    onClick={onClick}
    onArrowPress={onArrowPress}
    onNavFocus={onNavFocus}
    className={styles['tab']}
    variant="outline"
    size="sm"
  >
    {label}
  </Button>
);

// ─── Main episode selector ────────────────────────────────────

interface SheetEpisodeSelectorProps {
  isFocused?: boolean;
}

const SheetEpisodeSelector = observer(({ isFocused = false }: SheetEpisodeSelectorProps) => {
  const { episodes, episodesCount, isFetchingEpisodes, nextEventForSeries } =
    rootStore.episodesStore;
  const { sheet } = rootStore.sheetStore;
  const { handlePlay } = useStreamActions();
  const [lastStableEpisodesCount, setLastStableEpisodesCount] = React.useState(episodesCount);

  const activeTab = uiStore.activeEpisodeTab;

  const { seasons } = rootStore.seasonsStore;
  const hasSeasonTabs = seasons && seasons.length > 1;
  const hasUpcoming = Boolean(nextEventForSeries?.isNextEventforSeries);
  const providersVersionsDialog = uiStore.toggles.providersVersionsDialog;
  const shouldHideSeasonTabs = hasUpcoming && activeTab === 'coming-soon';
  const setNPVRStream = useSetAtom(NPVRStreamAtom);
  const { programsheet } = useQuery<QueryParams>();
  const { subProviderId } = decodeProgramSheetParams(programsheet);
  const isNPVR = subProviderId === 'NPVR';

  const { ref, focusKey, hasFocusedChild } = useFocusable({
    trackChildren: true,
    saveLastFocusedChild: true,
    isFocusBoundary: true,
    focusBoundaryDirections: ['left', 'right'],
    focusKey: FocusKeysSheet.EPISODE_SELECTOR,
    preferredChildFocusKey: shouldHideSeasonTabs
      ? 'EPISODE_SELECTOR_TAB_COMING_SOON'
      : hasSeasonTabs
      ? FocusKeysSheet.EPISODE_SELECTOR_SEASON_TABS
      : FocusKeysSheet.EPISODE_SELECTOR_LIST
  });

  // Mirror the original sheet-episodes behaviour: toggle the sheet-wide
  // focusEpisodes state whenever this section gains or loses a focused child.
  React.useEffect(() => {
    if (hasFocusedChild || providersVersionsDialog) {
      uiStore.toggleFocus(true);
    }
  }, [hasFocusedChild, providersVersionsDialog]);

  React.useEffect(() => {
    if (!isFetchingEpisodes && !hasUpcoming && activeTab === 'coming-soon') {
      uiStore.setActiveEpisodeTab('episodes');
    }
  }, [activeTab, hasUpcoming, isFetchingEpisodes]);

  const regularEpisodes = React.useMemo(
    () => episodes.filter((ep) => !ep.isNextEventforSeries),
    [episodes]
  );

  const upcomingEpisodes = React.useMemo(() => {
    return nextEventForSeries?.isNextEventforSeries ? [nextEventForSeries] : [];
  }, [nextEventForSeries]);
  const visibleEpisodes = activeTab === 'coming-soon' ? upcomingEpisodes : regularEpisodes;

  const handleSetFocusedEpisode = React.useCallback((episode: EpisodeExtended) => {
    rootStore.episodesStore.setFocusedEpisode(
      episode.episodeId,
      episode.isNextEventforSeries,
      episode.episodeTitle
    );
    isNPVR && setNPVRStream({ episodeId: episode.episodeId, focusKey: getCurrentFocusKey() });
    rootStore.providerVersionStore.setProvidersData(episode.providers);
  }, []);

  const resetEpisodeToPlay = () => {
    const providers: EpisodeExtended['providers'] = JSON.parse(JSON.stringify(sheet.providers));
    rootStore.providerVersionStore.setProvidersData(providers);
  };

  const handleMouseEnterEpisode = React.useCallback(
    (episode: EpisodeExtended) => {
      handleSetFocusedEpisode(episode);
    },
    [handleSetFocusedEpisode]
  );

  const handleMouseEnterEpisodeList = React.useCallback(
    (episode: EpisodeExtended) => {
      handleSetFocusedEpisode(episode);
    },
    [handleSetFocusedEpisode]
  );

  const handleSwitchToEpisodes = React.useCallback(
    () => uiStore.setActiveEpisodeTab('episodes'),
    []
  );
  const handleSwitchToComingSoon = React.useCallback(
    () => uiStore.setActiveEpisodeTab('coming-soon'),
    []
  );

  const handleEpisodesTabFocus = React.useCallback(() => {
    uiStore.setActiveEpisodeTab('episodes');
  }, []);

  const handleComingSoonTabFocus = React.useCallback(() => {
    uiStore.setActiveEpisodeTab('coming-soon');
  }, []);

  const handleTabArrowPress = React.useCallback((direction: string) => {
    if (direction !== 'up') return true;

    const actionFocusOrder: FocusKeysSheet[] = [
      FocusKeysSheet.FOCUS_KEY_WATCH_LIVE,
      FocusKeysSheet.FOCUS_KEY_CATCH_UP,
      FocusKeysSheet.FOCUS_KEY_RECORD,
      FocusKeysSheet.FOCUS_KEY_FAVORITE,
      FocusKeysSheet.FOCUS_KEY_TRAILER
    ];

    const nextFocus = actionFocusOrder.filter((key) => doesFocusableExist(key)).pop();
    uiStore.toggleFocus(false);
    setFocus(nextFocus ?? FocusKeysSheet.FOCUS_KEY_SHEET_DESCRIPTION);

    return false;
  }, []);

  const handleEpisodesTabArrowPress = React.useCallback(
    (direction: string) => {
      if (direction === 'left') return false;
      return handleTabArrowPress(direction);
    },
    [handleTabArrowPress]
  );

  React.useEffect(() => {
    if (!isFetchingEpisodes) {
      setLastStableEpisodesCount(episodesCount);
    }
  }, [episodesCount, isFetchingEpisodes]);

  // Only show the skeleton once a fetch has been running for a bit, so quick
  // season switches swap episodes directly instead of flashing a skeleton.
  const [showLoadingSkeleton, setShowLoadingSkeleton] = React.useState(false);

  useTimeout(() => setShowLoadingSkeleton(true), isFetchingEpisodes ? 200 : null);

  React.useEffect(() => {
    if (!isFetchingEpisodes) {
      setShowLoadingSkeleton(false);
    }
  }, [isFetchingEpisodes]);

  const episodesCountLabel =
    isFetchingEpisodes && episodesCount === 0 ? lastStableEpisodesCount : episodesCount;

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        ref={ref}
        className={clsx(styles['episode-selector'], {
          [styles['episode-selector--focused']]: isFocused
        })}
      >
        <div className={styles['tabs']}>
          <TabButton
            label={`Episoder (${episodesCountLabel})`}
            active={activeTab === 'episodes'}
            focusKey="EPISODE_SELECTOR_TAB_EPISODES"
            onClick={handleSwitchToEpisodes}
            onArrowPress={handleEpisodesTabArrowPress}
            onNavFocus={handleEpisodesTabFocus}
          />
          {hasUpcoming && (
            <TabButton
              label="Kommer snart"
              active={activeTab === 'coming-soon'}
              focusKey="EPISODE_SELECTOR_TAB_COMING_SOON"
              onClick={handleSwitchToComingSoon}
              onArrowPress={handleTabArrowPress}
              onNavFocus={handleComingSoonTabFocus}
            />
          )}
        </div>

        <SheetEpisodeSelectorSeasonTabs hidden={shouldHideSeasonTabs || !hasSeasonTabs} />

        <div className={styles['episode-content-frame']}>
          <div className={clsx(styles['episode-content-frame__list'], {
            [styles['episode-content-frame__list--offset']]: shouldHideSeasonTabs || !hasSeasonTabs
          })}>
            <SheetEpisodeSelectorList
              episodes={visibleEpisodes}
              onFocus={handleSetFocusedEpisode}
              onMouseEnterEpisode={IS_BROWSER ? handleMouseEnterEpisode : undefined}
              onMouseLeaveEpisode={IS_BROWSER ? resetEpisodeToPlay : undefined}
              onMouseEnterList={IS_BROWSER ? handleMouseEnterEpisodeList : undefined}
              onEnterPress={handlePlay}
              onClick={handlePlay}
              isFutureEpisodes={shouldHideSeasonTabs}
              hasSeasonTabs={hasSeasonTabs}
              isSelectorFocused={isFocused}
              isLoading={showLoadingSkeleton}
              upFocusKey={
                shouldHideSeasonTabs
                  ? hasUpcoming
                    ? 'EPISODE_SELECTOR_TAB_COMING_SOON'
                    : 'EPISODE_SELECTOR_TAB_EPISODES'
                  : hasSeasonTabs
                  ? FocusKeysSheet.EPISODE_SELECTOR_SEASON_TABS
                  : 'EPISODE_SELECTOR_TAB_EPISODES'
              }
            />
          </div>

          <div className={styles['episode-content-frame__detail']}>
            <SheetEpisodeSelectorDetail isVisible={true} isLoading={showLoadingSkeleton} />
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  );
});

SheetEpisodeSelector.displayName = 'SheetEpisodeSelector';

export default SheetEpisodeSelector;
